
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProfileSection } from "@/components/settings/ProfileSection";
import { NotificationsSection } from "@/components/settings/NotificationsSection";
import { SecuritySection } from "@/components/settings/SecuritySection";
import { BillingSection } from "@/components/settings/BillingSection";
import { LegalSection } from "@/components/settings/LegalSection";

interface Profile {
  name: string | null;
  email: string | null;
  industry: string | null;
  email_reminders: boolean;
  sms_notifications: boolean;
  phone: string | null;
}

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile>({
    name: "",
    email: "",
    industry: "",
    email_reminders: false,
    sms_notifications: false,
    phone: "",
  });

  useEffect(() => {
    async function getProfile() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate("/auth");
          return;
        }

        const { data, error } = await supabase
          .from("profiles")
          .select("name, email, industry, email_reminders, sms_notifications, phone")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        if (data) {
          setProfile({
            name: data.name || "",
            email: data.email || "",
            industry: data.industry || "",
            email_reminders: data.email_reminders || false,
            sms_notifications: data.sms_notifications || false,
            phone: data.phone || "",
          });
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not load profile settings",
        });
      } finally {
        setLoading(false);
      }
    }

    getProfile();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("profiles")
        .update({
          name: profile.name,
          industry: profile.industry,
          email_reminders: profile.email_reminders,
          sms_notifications: profile.sms_notifications,
          phone: profile.phone,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile settings updated successfully",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update profile settings",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (key: string, value: string | boolean) => {
    setProfile(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="container max-w-3xl py-10 space-y-8">
      <Card>
        <ProfileSection
          profile={profile}
          loading={loading}
          onProfileChange={handleProfileChange}
          onCancel={() => navigate(-1)}
          onSubmit={handleSubmit}
        />
      </Card>

      <Card>
        <NotificationsSection
          profile={profile}
          onProfileChange={handleProfileChange}
        />
      </Card>

      <Card>
        <SecuritySection />
      </Card>

      <Card>
        <BillingSection />
      </Card>

      <Card>
        <LegalSection />
      </Card>
    </div>
  );
}
